import React, { useMemo } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import format from 'date-fns/format';
import isDate from 'lodash/isDate';

// GLOBAL VARIABLES
import { BUTTON } from 'defaults.js';
import { DATE_FORMATS } from 'globals.js';

// GLOBAL FUNCTIONS
import {
  apiFetch,
  documentChunkFetch,
  documentFetch,
  getFileType,
  modalFunctions,
  shouldOpenFile,
  viewFile
} from 'functions.js';

// GLOBAL COMPONENTS
import Button from 'core/tools/Button';

// STYLES
import styles from './uploadItem.module.scss';

// MAIN COMPONENT
const UploadItem = ({
  className,
  meta: { fileName = 'file', uploadedBy, uploadedOn},
  allowView = true,
  viewArgs,
  allowDelete = true,
  deleteArgs,
  shouldChunkDownload = true
}) => {

  // MEMOS
  const attribution = useMemo(() => {
    const attribution = [];
    if (uploadedBy) attribution.push(`by ${uploadedBy}`);
    if (uploadedOn && isDate(new Date(uploadedOn)))
      attribution.push(`on ${format(new Date(uploadedOn), DATE_FORMATS.dateTime)}`);
    return attribution;
  }, [uploadedBy, uploadedOn]);

  const handleDelete = () => {
    const { endpoint, ...args } = deleteArgs;
    modalFunctions.add({
      type: 'confirmation',
      title: 'Confirm Delete',
      body: 'Are you sure you want to delete this attachment?',
      closeButton: false,
      continueButton: {
        label: 'Confirm',
        onClick: () => {
          apiFetch({
            method: 'DELETE',
            endpoint: endpoint,
            loadingMessage: 'Deleting attachment.',
            errorMessage: 'Unable to delete attachment.',
            ...args
          });
        }
      }
    });
  };

  const handleDownload = () => {
    const { endpoint, ...args } = viewArgs || {};
    const downloadFn = shouldChunkDownload ? documentChunkFetch : documentFetch;
    const mimeType = getFileType(fileName);
    downloadFn({
      endpoint: endpoint,
      mimeType,
      loadingMessage: shouldOpenFile(mimeType) ? 'Opening file' : 'Downloading file',
      errorMessage: 'Unable to download file.',
      onSuccess: r => viewFile({ ...r, fileName }),
      ...args
    });
  };

  // RENDER
  return (
    <div className={clsx(styles.item, className)}>
      {allowDelete &&
        <Button.Link className="me-2" icon={BUTTON.delete.icon} onClick={() => handleDelete()} />
      }
      <div className="d-flex align-items-center flex-wrap">
        {allowView ? (
          <Button.Link className="me-2" label={fileName} onClick={() => handleDownload()} />
        ) : (
          <span className="me-2">fileName</span>
        )}
        {attribution.length > 0 && <small className={styles.attribution}>({attribution.join(' ')})</small>}
      </div>
    </div>
  );
};

export default UploadItem;
